import React, { useEffect, useMemo, useState } from "react";
import { useTable, useSortBy, usePagination, useRowSelect } from "react-table";
import "tailwindcss/tailwind.css";
import { Empty } from "antd";
import "../../styles.css";
import Vectorsorting from "../../assets/Vectorsorting.svg";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CustomPagination from "../../components/CustomWidgets/CustomPagination";
import { useSelector } from "react-redux";
import { checkPermission } from "../../Utils/permissionHelper";
import ShimmerLoader from "../../loading";
import arrowright from "../../assets/arrowright.svg";
import i18n from "../../i18n";

const AllSessionHistory = ({
  additionalData,
  loading,
  pagination,
  handleCloseSessionClick,
  onRowClick
}) => {
  const { t } = useTranslation();
  const [flag, setflag] = useState(false);
  const navigate = useNavigate();
  const isGinne = useSelector((state) => state?.geneLoginReducer?.isGinneLogin);
  const allStores = useSelector(
    (state) => state?.storeReducer?.selectedStore?.is_select_all_store
  );

  const data = useMemo(() => additionalData || [], [additionalData]);

  useEffect(() => {
    if (additionalData?.length === 0 && !flag) {
      const timer = setTimeout(() => setflag(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [additionalData, flag]);

  const columns = useMemo(
    () => [
      {
        Header: t("Session ID"),
        accessor: "session_id",
        width: "8%",
        Cell: ({ value }) => (
          <span className="font-medium text-gray-700">{value || "--"}</span>
        ),
      },
      {
        Header: t("Cashier Name"),
        accessor: "user_name",
        width: "19%",
        Cell: ({ value }) => (
          <div className="text-start">{value || "--"}</div>
        ),
      },
      {
        Header: t("Orders"),
        accessor: "order_count",
        width: "6%",
        Cell: ({ value }) => value ?? "0",
      },
      {
        Header: t("Opening Date"),
        accessor: "start_at",
        width: "14%",
        Cell: ({ value }) => value || "--",
      },
      {
        Header: t("Closing Date"),
        accessor: "stop_at",
        width: "14%",
        Cell: ({ value }) => value || "--",
      },
      {
        Header: t("Total Sales"),
        accessor: "total_sales",
        width: "10%",
        Cell: ({ value }) => `EGP ${Number(value || 0).toFixed(2)}`,
      },
      {
        Header: t("Closing Balance"),
        accessor: "closing_balance",
        width: "12%",
        Cell: ({ value }) => (
          <span className="font-semibold">
            {`EGP ${Number(value || 0).toFixed(2)}`}
          </span>
        ),
      },
      {
        Header: t("Status"),
        accessor: "status",
        width: "10%",
        Cell: ({ value }) => {
          const isOpened = value?.toLowerCase() === "opened";
          return (
            <span
              className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                isOpened
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-green-100 text-green-800"
              }`}
            >
              {isOpened ? t("Opened") : t("Closed")}
            </span>
          );
        },
      },
      {
        Header: t("Actions"),
        accessor: "actions",
        disableSortBy: true,
        width: "10%",
        Cell: ({ row }) => {
          const session = row.original;
          return session?.status?.toLowerCase() === "opened" ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (checkPermission("/pos/sessionHistory", isGinne)) {
                  handleCloseSessionClick(
                    session?.session_id,
                    session?.access_token
                  );
                }
              }}
              className="bg-primaryBlue text-white text-xs font-semibold px-3 py-1.5 rounded-lg"
            >
              {t("Close Session")}
            </button>
          ) : null;
        },
      },
      {
            id: "action",
            Header: "",
            disableSortBy: true,
            width: "6%",
            Cell: ({ row }) => (
              <button className="text-[#1b4497] rounded-lg">
                <img
                  src={arrowright}
                  alt="arrowright"
                  className={`${i18n.language === "en" ? "" : "rotate-180"}`}
                  height={3}
                  width={20}
                />
              </button>
            ),
          },
    ],
    [t, allStores]
  );

  const {
    headerGroups,
    prepareRow,
    page,
    nextPage,
    setPageSize,
    state: { pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageSize: 10 },
    },
    useSortBy,
    usePagination,
    useRowSelect
  );

  const [pageIndex, setpageIndex] = useState(1);

  useEffect(() => {
    const pageDetails = {
      limit: pageSize,
      offset: pageSize * (pageIndex - 1) < 0 ? 0 : pageSize * (pageIndex - 1),
    };
    if (pagination) pagination(pageDetails);
  }, [pageSize, pageIndex]);

  const handlePageSizeChange = (newPageSize) => {
    const maxPageIndex = Math.ceil(data.length / newPageSize);
    if (pageIndex > maxPageIndex) setpageIndex(maxPageIndex);
    setPageSize(newPageSize);
  };

  return (
    <div className="container flex flex-col mx-auto mt-4" style={{ maxWidth: "100%" }}>
      {(additionalData?.length === 0 && flag === false || loading) ? (
        <div className="h-screen w-[90%] mt-2">
          <ShimmerLoader />
        </div>
      ) : (
        <>
          {/* Header */}
          <div>
            <table
              className="font-normal max-w-[100%] overflow-hidden rounded-lg"
              style={{ tableLayout: "fixed" }}
            >
              <thead>
                {headerGroups.map((headerGroup) => {
                  const { key: headerKey, ...headerGroupProps } =
                    headerGroup.getHeaderGroupProps();
                  return (
                    <tr
                      key={headerKey}
                      {...headerGroupProps}
                      className="bg-[#78CFEC66] text-sm font-semibold overflow-hidden whitespace-nowrap"
                    >
                      {headerGroup.headers.map((column) => {
                        const { key: columnKey, ...columnProps } =
                          column.getHeaderProps(column.getSortByToggleProps());
                        return (
                          <th
                            key={columnKey}
                            {...columnProps}
                            className="py-3 whitespace-nowrap"
                            style={{ width: column.width }}
                          >
                            <div className="flex flex-row items-center ms-2 text-sm leading-tight">
                              {column.render("Header")}
                              {column.canSort && (
                                <span className="ms-1">
                                  {column.isSorted ? (
                                    column.isSortedDesc ? (
                                      <img src={Vectorsorting} style={{ minHeight: 10, minWidth: 10 }} alt="vector" />
                                    ) : (
                                      <img className="rotate-180" src={Vectorsorting} alt="vector" />
                                    )
                                  ) : (
                                    <img src={Vectorsorting} alt="sortable" style={{ minHeight: 10, minWidth: 10, opacity: 0.3 }} />
                                  )}
                                </span>
                              )}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  );
                })}
              </thead>
            </table>
          </div>

          {/* Body */}
          <div className="scrollbar-none bg-white" style={{ overflowY: "auto", maxHeight: "450px" }}>
            <table
              className="font-normal max-w-[100%]  overflow-hidden rounded-lg"
              style={{ tableLayout: "fixed" }}
            >
              <tbody className="bg-[#F7F9FD] text-center overflow-hidden">
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        {...row.getRowProps()}
                        className="relative hover:bg-white cursor-pointer transition duration-300 border-b border-gray-300 fixed-row-height"
                        onClick={() =>
                                                onRowClick && onRowClick(row.original?.session_id)
                                            }
                      >
                        {row.cells.map((cell, index) => (
                          <td
                            key={index}
                            {...cell.getCellProps()}
                            className={`p-2 text-[#272728] text-sm font-normal ${
                              index < row.cells.length - 1 ? "relative" : ""
                            }`}
                            style={{ width: cell.column.width }}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    </React.Fragment>
                  );
                })}
                {page.length === 0 && (
                  <tr>
                    <td colSpan="100%">
                      <Empty className="mt-10" description={t("No data available")} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div>
            <CustomPagination
              data={additionalData?.length || 0}
              pageIndex={pageIndex}
              pageCount={Math.ceil((additionalData?.length || 0) / pageSize)}
              canPreviousPage={pageIndex !== 1}
              canNextPage={data?.length === pageSize}
              gotoPage={(p) => nextPage(p)}
              previousPage={() => setpageIndex(pageIndex - 1)}
              nextPage={() => setpageIndex(pageIndex + 1)}
              pageSize={pageSize}
              setPageSize={handlePageSizeChange}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default AllSessionHistory;